import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, Wrench, Wallet, PiggyBank, Banknote, Clock, Coins, FileSpreadsheet } from "lucide-react";
import api, { downloadBlob } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { PageHeader, SectionCard, StatCard, Money, Loading } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatTanggal } from "@/lib/format";
import { toast } from "sonner";

function monthStart() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

const PRESETS = [
  { id: "month", label: "Bulan Ini" },
  { id: "q", label: "3 Bulan Terakhir" },
  { id: "year", label: "Tahun Ini" },
  { id: "custom", label: "Kustom" },
];

export default function RekapKeuangan() {
  const { projectId, projectParam } = useProjects();
  const [preset, setPreset] = useState("month");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());

  const applyPreset = (id) => {
    setPreset(id);
    const now = new Date();
    if (id === "month") { setFrom(monthStart()); setTo(today()); }
    else if (id === "q") { setFrom(new Date(now.getFullYear(), now.getMonth() - 2, 1).toISOString().slice(0, 10)); setTo(today()); }
    else if (id === "year") { setFrom(new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10)); setTo(today()); }
  };

  const { data, isLoading } = useQuery({
    queryKey: ["recap", projectId, from, to],
    queryFn: async () => (await api.get("/recap", { params: { ...projectParam, date_from: from, date_to: to } })).data,
  });

  const exportRecap = async (fmt) => {
    try {
      const res = await api.get(`/recap/export.${fmt}`, {
        params: { ...projectParam, date_from: from, date_to: to },
        responseType: "blob",
      });
      downloadBlob(res.data, `rekap-keuangan.${fmt}`);
    } catch { toast.error("Gagal mengekspor rekap."); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Rekap Keuangan" description="Rekap dana masuk — terpisah antara investor, perawatan, IPL, dan DP hangus."
        actions={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => exportRecap("csv")} data-testid="recap-export-csv"><FileSpreadsheet className="mr-2 h-4 w-4" /> CSV</Button>
          <Button variant="outline" onClick={() => exportRecap("xlsx")} data-testid="recap-export-xlsx"><FileSpreadsheet className="mr-2 h-4 w-4" /> Excel</Button>
        </div>} />

      <SectionCard testid="recap-filter">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <Button
                key={p.id}
                variant={preset === p.id ? "default" : "outline"}
                size="sm"
                onClick={() => applyPreset(p.id)}
                data-testid={`recap-preset-${p.id}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
          {preset === "custom" && (
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-xs">Dari</Label>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="recap-date-from" />
              </div>
              <div>
                <Label className="text-xs">Sampai</Label>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="recap-date-to" />
              </div>
            </div>
          )}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Periode: {formatTanggal(from)} s/d {formatTanggal(to)} — diakui berdasarkan tanggal pelunasan.
        </p>
      </SectionCard>

      {isLoading || !data ? (
        <Loading />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatCard testid="recap-investor" label="A. Bagian Investor (80%)" value={<Money value={data.investor.amount} />} icon={TrendingUp} accent="#047857" hint="80% × komponen sewa lunas" />
            <StatCard testid="recap-care" label="B. Dana Perawatan (20%)" value={<Money value={data.care.amount} />} icon={Wrench} accent="#0369a1" hint="20% × komponen sewa lunas" />
            <StatCard testid="recap-ipl" label="C. Pendapatan IPL" value={<Money value={data.ipl.amount} />} icon={Wallet} accent="#7e22ce" hint="Terpisah dari investor & perawatan" />
            <StatCard testid="recap-dp" label="D. DP Hangus" value={<Money value={data.dp_forfeit.amount} />} icon={PiggyBank} accent="#dd6030" hint="Rp500.000 per booking hangus" />
            <StatCard testid="recap-extra" label="E. Biaya Tambahan (Pengelola)" value={<Money value={data.extra_fees?.amount || 0} />} icon={Coins} accent="#b45309" hint="Pendapatan pengelola, di luar split 80/20" />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <SectionCard title="Total Sewa Diakui" testid="recap-rent-total">
              <div className="text-2xl font-extrabold tabular-nums"><Money value={data.rent_recognized} /></div>
              <p className="mt-1 text-xs text-muted-foreground">Dasar pembagian 80/20 (investor + perawatan).</p>
            </SectionCard>
            <SectionCard title="Penerimaan Uang Aktual" testid="recap-cash">
              <div className="flex items-center gap-2 text-2xl font-extrabold tabular-nums">
                <Banknote className="h-5 w-5 text-muted-foreground" /><Money value={data.cash_received.total} />
              </div>
              <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                <div className="flex justify-between"><span>Transfer</span><Money value={data.cash_received.transfer} /></div>
                <div className="flex justify-between"><span>Tunai</span><Money value={data.cash_received.cash} /></div>
              </div>
            </SectionCard>
            <SectionCard title="DP Menunggu Penyelesaian" testid="recap-dp-pending">
              <div className="flex items-center gap-2 text-2xl font-extrabold tabular-nums">
                <Clock className="h-5 w-5 text-muted-foreground" /><Money value={data.dp_pending.amount} />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{data.dp_pending.count} booking belum lunas (belum jadi pendapatan).</p>
            </SectionCard>
          </div>

          <div className="rounded-xl border border-border bg-accent/40 p-4 text-sm text-foreground">
            <p className="font-medium">Catatan aturan rekap</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
              <li>DP yang dikonversi menjadi pembayaran sewa tidak dihitung lagi sebagai pendapatan tambahan.</li>
              <li>Hanya rekap dana masuk — tidak ada modul pengeluaran atau saldo dana setelah biaya.</li>
              <li>IPL tidak masuk dasar potongan perawatan 20% dan tidak masuk pendapatan investor.</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
